"use strict";

const swiftrefInput = require("src/components/form/input");
const baseTemplate = document.createElement("template");
baseTemplate.innerHTML = require("./template");
const typography = require("!!css-loader!../../../tokens/typography.css").default;

const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

class VanillaDatePicker extends swiftrefInput {
    constructor() {
        super(baseTemplate);

        /**
         * Applying styles inside the shadow DOM:
         * @see https://developer.mozilla.org/en-US/docs/Web/API/Web_components/Using_shadow_DOM#applying_styles_inside_the_shadow_dom
         */
        const sheet = new CSSStyleSheet();
        sheet.replaceSync(typography);
        this.root.adoptedStyleSheets = [sheet];

        // State
        this._isOpen = false;
        this._focusDate = new Date();
        this._selectedDate = null;
        this._selectedDates = [];
        this._mode = 'single'; // 'single', 'multiple', or 'range'
        this._rangeStart = null;
        this._currentYear = this._focusDate.getUTCFullYear();
        this._currentMonth = this._focusDate.getUTCMonth();
        this._mouseDown = false;

        // DOM elements references
        this._elements = {
            input: null,
            calendar: null,
            monthYear: null,
            daysGrid: null,
            prevMonthBtn: null,
            nextMonthBtn: null,
            prevYearBtn: null,
            nextYearBtn: null
        };

        // Bind methods
        this._handleInputClick = this._handleInputClick.bind(this);
        this._handleOutsideClick = this._handleOutsideClick.bind(this);
        this._handlePrevMonthClick = this._handlePrevMonthClick.bind(this);
        this._handleNextMonthClick = this._handleNextMonthClick.bind(this);
        this._handlePrevYearClick = this._handlePrevYearClick.bind(this);
        this._handleNextYearClick = this._handleNextYearClick.bind(this);
        this._handleDayClick = this._handleDayClick.bind(this);
        this._handleKeyDown = this._handleKeyDown.bind(this);
        this.timePickerChange = this.timePickerChange.bind(this);
        this._handleTodayButtonOnClick = this._handleTodayButtonOnClick.bind(this);
        this.clear = this.clear.bind(this);
        this.close = this.close.bind(this);



        // Store references to DOM elements
        this._elements.input = this.root.querySelector(".swiftref-input input");
        this._elements.calendarIcon = this.root.querySelector('swiftref-icon[kind="calendar"]');
        this._elements.calendar = this.shadowRoot.querySelector('.datepicker-calendar');
        this._elements.monthYear = this.shadowRoot.querySelector('.month-year');
        this._elements.daysGrid = this.shadowRoot.querySelector('.days-grid');
        this._elements.prevMonthBtn = this.shadowRoot.querySelector('.prev-month');
        this._elements.nextMonthBtn = this.shadowRoot.querySelector('.next-month');
        this._elements.prevYearBtn = this.shadowRoot.querySelector('.prev-year');
        this._elements.nextYearBtn = this.shadowRoot.querySelector('.next-year');
        this._elements.timePicker = this.shadowRoot.querySelector('.time-input');
        this._elements.todayBtn = this.shadowRoot.querySelector('.today-btn');
        this._elements.clearBtn = this.shadowRoot.querySelector('.clear-btn');
        this._elements.doneBtn = this.shadowRoot.querySelector('.done-btn');

        // Apply any theme
        this._applyTheme();

        // Set initial display
        this._updateCalendarDisplay();

        // Initialize with any provided attributes
        this._initializeFromAttributes();

        // todo simone try to directly edit the input
        // todo try inside dialog layout

    }

    // Lifecycle methods
    connectedCallback() {
        super.connectedCallback();
        this._setupEventListeners();

        // Initial calendar render
        this._renderCalendarDays();

        // Fire ready event
        this.dispatchEvent(new CustomEvent('datepicker-ready'));
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this._removeEventListeners();
    }

    static get observedAttributes() {
        return super.observedAttributes.concat([
            'date-format', 'min-date', 'max-date', 'mode',
            'enable-time', 'inline', 'default-date', 'theme'
        ]);
    }

    attributeChangedCallback(name, oldValue, newValue) {
        super.attributeChangedCallback(name, oldValue, newValue);
        if (oldValue === newValue) return;

        switch (name) {
            case 'date-format':
                this._dateFormat = newValue || 'mm/dd/yyyy';
                this._updateInputValue();
                break;
            case 'min-date':
                this._minDate = newValue ? this._parseDateAsUTC(newValue) : null; // fix
                this._renderCalendarDays();
                break;
            case 'max-date':
                this._maxDate = newValue ? this._parseDateAsUTC(newValue) : null; // fix
                this._renderCalendarDays();
                break;
            case 'mode':
                this._mode = newValue || 'single';
                if (this._mode === 'single') {
                    this._selectedDates = this._selectedDate ? [this._selectedDate] : [];
                }
                this._renderCalendarDays();
                break;
            case 'enable-time':
                this._enableTime = this.hasAttribute('enable-time');

                this._elements.timePicker.removeEventListener("change", this.timePickerChange)
                if (this._enableTime) {
                    this._elements.timePicker.addEventListener("change", this.timePickerChange)
                    this._updateInputValue();
                    this._updateCalendarDisplay();
                }

                break;
            case 'inline':
                this._inline = this.hasAttribute('inline');
                this._updateCalendarDisplay();
                break;
            case 'value':
                if (newValue) {
                    const date = this._parseDateAsUTC(newValue); // fix
                    if (!isNaN(date.getTime())) {
                        this._setDate(date);
                    }
                }
                break;
            case 'theme':
                this._theme = newValue || 'light';
                this._applyTheme();
                break;
        }
    }

    // Public API
    get value() {
        return this._elements.input ? this._elements.input.value : '';
    }

    set value(val) {
        if (!val) {
            this.clear();
            return;
        }

        const date = this._parseDateAsUTC(val); // fix
        if (!isNaN(date.getTime())) {
            this._setDate(date);
        }
    }

    get selectedDates() {
        return [...this._selectedDates];
    }

    open() {
        if (this._isOpen || this._inline || this.hasAttribute("disabled")) return;

        this._isOpen = true;
        this._elements.calendar.classList.add('open');
        this._focusDate = this._selectedDate || new Date();
        this._currentYear = this._focusDate.getUTCFullYear();
        this._currentMonth = this._focusDate.getUTCMonth();
        this._renderCalendarDays();

        this._elements.calendar.setAttribute('tabindex', '-1');
        // Fire open event
        this.dispatchEvent(new CustomEvent('datepicker-opened'));
    }

    close() {
        if (!this._isOpen || this._inline) return;

        this._isOpen = false;
        this._elements.calendar.classList.remove('open');

        // Fire close event
        this.dispatchEvent(new CustomEvent('datepicker-closed'));
    }

    clear() {
        this._selectedDate = null;
        this._selectedDates = [];
        this._rangeStart = null;
        this._updateInputValue();
        this._renderCalendarDays();

        // Fire change event
        this._fireChangeEvent();
    }


    _setupEventListeners() {
        this._elements.input.addEventListener('click', this._handleInputClick);
        this._elements.calendarIcon.addEventListener('click', this._handleInputClick);
        this._elements.prevMonthBtn.addEventListener('click', this._handlePrevMonthClick);
        this._elements.nextMonthBtn.addEventListener('click', this._handleNextMonthClick);
        this._elements.prevYearBtn.addEventListener('click', this._handlePrevYearClick);
        this._elements.nextYearBtn.addEventListener('click', this._handleNextYearClick);
        this._elements.todayBtn.addEventListener("click", this._handleTodayButtonOnClick);
        this._elements.clearBtn.addEventListener("click", this.clear);
        this._elements.doneBtn.addEventListener("click", this.close);
        this.shadowRoot.addEventListener('keydown', this._handleKeyDown);
        // Attach global event for closing when clicking outside
        if (!this._inline) {
            document.addEventListener('mousedown', this._handleOutsideClick);
        }
    }

    _removeEventListeners() {
        this._elements.input.removeEventListener('click', this._handleInputClick);
        this._elements.calendarIcon.removeEventListener('click', this._handleInputClick);
        this._elements.prevMonthBtn.removeEventListener('click', this._handlePrevMonthClick);
        this._elements.nextMonthBtn.removeEventListener('click', this._handleNextMonthClick);
        this._elements.prevYearBtn.removeEventListener('click', this._handlePrevYearClick);
        this._elements.nextYearBtn.removeEventListener('click', this._handleNextYearClick);
        this._elements.todayBtn.removeEventListener("click", this._handleTodayButtonOnClick);
        this._elements.clearBtn.removeEventListener("click", this.clear);
        this._elements.doneBtn.removeEventListener("click", this.close);
        this.shadowRoot.removeEventListener('keydown', this._handleKeyDown);
        document.removeEventListener('mousedown', this._handleOutsideClick);
    }

    _handleInputClick(e) {
        if (this._isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    _handleOutsideClick(e) {
        const path = e.composedPath ? e.composedPath() : e.path || [];
        if (!path.includes(this) && this._isOpen) {
            this.close();
        }
    }

    _handlePrevMonthClick() {
        this._currentMonth--;
        if (this._currentMonth < 0) {
            this._currentMonth = 11;
            this._currentYear--;
        }
        this._renderCalendarDays();
    }

    _handleNextMonthClick() {
        this._currentMonth++;
        if (this._currentMonth > 11) {
            this._currentMonth = 0;
            this._currentYear++;
        }
        this._renderCalendarDays();
    }

    _handlePrevYearClick() {
        this._currentYear--;
        this._renderCalendarDays();
    }

    _handleNextYearClick() {
        this._currentYear++;
        this._renderCalendarDays();
    }

    _handleTodayButtonOnClick() {
        const today = new Date();
        this._currentYear = today.getUTCFullYear();
        this._currentMonth = today.getUTCMonth();
        this._setDate(today);
        this._renderCalendarDays();
    }

    _handleDayClick(e) {
        const day = e.currentTarget;
        if (day.classList.contains('disabled')) return;

        const date = new Date(Date.UTC(parseInt(day.dataset.year), parseInt(day.dataset.month), parseInt(day.dataset.day)));

        if (this._mode === 'single') {
            this._setDate(date);
            if (!this._inline && !this._enableTime) {
                this.close();
            }
        } else if (this._mode === 'multiple') {
            const dateStr = this._formatDateForComparison(date);
            const index = this._selectedDates.findIndex(d => this._formatDateForComparison(d) === dateStr);

            if (index !== -1) {
                this._selectedDates.splice(index, 1);
            } else {
                this._selectedDates.push(date);
            }

            this._selectedDate = date;
            this._updateInputValue();
            this._renderCalendarDays();
            this._fireChangeEvent();
        } else if (this._mode === 'range') {
            if (!this._rangeStart || this._selectedDates.length === 2) {
                this._rangeStart = date;
                this._selectedDates = [date];
            } else {
                // Sort dates to ensure start date < end date
                if (date < this._rangeStart) {
                    this._selectedDates = [date, this._rangeStart];
                } else {
                    this._selectedDates = [this._rangeStart, date];
                }
                this._rangeStart = null;
                if (!this._inline && !this._enableTime) {
                    this.close();
                }
            }

            this._selectedDate = date;
            this._updateInputValue();
            this._renderCalendarDays();
            this._fireChangeEvent();
        }
    }

    _handleKeyDown(e) {

        if (!this._isOpen) return;

        // Get the currently focused day or the selected date, or default to middle of current month
        let focusedDay;
        // if (this._selectedDate &&
        //     this._selectedDate.getUTCMonth() === this._currentMonth &&
        //     this._selectedDate.getUTCFullYear() === this._currentYear) {
        //     focusedDay = this._selectedDate.getUTCDate();
        // } else {
        // Find the first day that has tabindex="0" (currently focused)
        //const focusedElement = this.shadowRoot.querySelector('.day[tabindex="0"]');
        const focusedElement = this.shadowRoot.querySelector('.day.hover');

        if (focusedElement) {
            focusedDay = parseInt(focusedElement.dataset.day, 10);
        } else {
            // Default to middle of month if nothing is focused or selected
            focusedDay = 15;
        }
        // }


        let newDate = new Date(this._currentYear, this._currentMonth, focusedDay);
        let needsUpdate = false;

        switch (e.key) {
            case 'ArrowLeft':
                newDate.setUTCDate(newDate.getUTCDate() - 1);
                needsUpdate = true;
                break;
            case 'ArrowRight':
                newDate.setUTCDate(newDate.getUTCDate() + 1);
                needsUpdate = true;
                break;
            case 'ArrowUp':
                newDate.setUTCDate(newDate.getUTCDate() - 7);
                needsUpdate = true;
                break;
            case 'ArrowDown':
                newDate.setUTCDate(newDate.getUTCDate() + 7);
                needsUpdate = true;
                break;
            case 'Enter':
                // Select the currently focused day
                const focusedElement = this.shadowRoot.querySelector('.day[tabindex="0"]');
                if (focusedElement) {
                    // Simulate a click on the focused day
                    focusedElement.click();
                }
                return;
            case 'Escape':
                this.close();
                return;
            default:
                return;
        }

        if (needsUpdate) {
            e.preventDefault();

            // Update the current month and year if the new date is in a different month
            this._currentYear = newDate.getUTCFullYear();
            this._currentMonth = newDate.getUTCMonth();
            this._renderCalendarDays();

            // Set focus on the new day after rendering
            this._focusDay(newDate.getUTCDate());
        }
    }

    _focusDay(day) {
        // Remove tabindex="0" from all days
        const days = this.shadowRoot.querySelectorAll('.day');
        days.forEach(dayElement => {
            dayElement.setAttribute('tabindex', '-1');
            dayElement.classList.remove("hover")
        });

        // Find the specific day that matches the current month/year and set tabindex="0"
        const dayToFocus = Array.from(days).find(dayElement => {
            return parseInt(dayElement.dataset.day, 10) === day &&
                parseInt(dayElement.dataset.month, 10) === this._currentMonth &&
                parseInt(dayElement.dataset.year, 10) === this._currentYear;
        });

        if (dayToFocus) {
            dayToFocus.setAttribute('tabindex', '0');
            dayToFocus.classList.add("hover")
            dayToFocus.focus();
        }
    }

    _initializeFromAttributes() {
        this._dateFormat = this.getAttribute('date-format') || 'mm/dd/yyyy';
        this._minDate = this.hasAttribute('min-date') ? this._parseDateAsUTC(this.getAttribute('min-date')) : null; // fix
        this._maxDate = this.hasAttribute('max-date') ? this._parseDateAsUTC(this.getAttribute('max-date')) : null; // fix
        this._mode = this.getAttribute('mode') || 'single';
        this._enableTime = this.hasAttribute('enable-time');
        this._inline = this.hasAttribute('inline');
        this._theme = this.getAttribute('theme') || 'light';

        // Handle default date if provided
        const defaultDate = this.getAttribute('default-date');
        if (defaultDate) {
            const date = this._parseDateAsUTC(defaultDate); // fix
            if (!isNaN(date.getTime())) {
                this._setDate(date);
            }
        }

        // Update display
        this._updateCalendarDisplay();
    }

    _updateCalendarDisplay() {
        if (this._inline) {
            this._elements.calendar.classList.add('inline');
            this._elements.calendar.classList.add('open');
            this._isOpen = true;
        }

        const timeContainer = this.shadowRoot.querySelector('.time-container');
        if (this._enableTime) {
            timeContainer.style.display = 'block';
        } else {
            timeContainer.style.display = 'none';
        }
    }

    _applyTheme() {
        if (this._theme === 'dark') {
            this.classList.add('dark');
        } else {
            this.classList.remove('dark');
        }
    }

    timePickerChange (event) {
        const [hours, minutes, seconds] = event.target.value.split(":");

        if (!this._selectedDate) {
            this._selectedDate = new Date();
        }

        this._selectedDate.setUTCHours(parseInt(hours, 10), parseInt(minutes, 10));
        this._selectedDate.setUTCSeconds(parseInt(seconds));
        this._selectedDates[0] = this._selectedDate;
        this._updateInputValue();
        this._fireChangeEvent();
    }

    _renderCalendarDays() {
        const daysGrid = this._elements.daysGrid;
        daysGrid.innerHTML = '';

        // Update month/year display
        this._elements.monthYear.textContent = `${monthNames[this._currentMonth]} ${this._currentYear}`;

        // Get first day of month and number of days
        const firstDay = new Date(this._currentYear, this._currentMonth, 1);
        const lastDay = new Date(this._currentYear, this._currentMonth + 1, 0);

        /**
         * use getDate in order to get the last day in number without substraction hours.
         * E.g.: Mon Jun 30 2025 00:00:00 GMT+0200 (Central European Summer Time)
         * with getUTCDate gives: 29
         *
         * @type {number}
         */
        const daysInMonth = lastDay.getDate();

        // Get day of week of first day (0 = Sunday, 6 = Saturday)
        // const dayOfWeek = firstDay.getUTCDay();
        // JavaScript's getDay() returns: 0=Sunday, 1=Monday, 2=Tuesday, etc.
        // For Monday-first week, we need: 0=Monday, 1=Tuesday, etc., 6=Sunday
        let dayOfWeek = firstDay.getDay();
        dayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

        // Get days from previous month to fill first row
        const daysFromPrevMonth = dayOfWeek;
        const prevMonth = this._currentMonth === 0 ? 11 : this._currentMonth - 1;
        const prevMonthYear = this._currentMonth === 0 ? this._currentYear - 1 : this._currentYear;
        const daysInPrevMonth = new Date(prevMonthYear, prevMonth + 1, 0).getDate();

        // Add days from previous month
        for (let i = daysInPrevMonth - daysFromPrevMonth + 1; i <= daysInPrevMonth; i++) {
            const day = document.createElement('div');
            day.className = 'day other-month';
            day.textContent = i;
            day.dataset.day = i;
            day.dataset.month = prevMonth;
            day.dataset.year = prevMonthYear;
            daysGrid.appendChild(day);

            day.addEventListener('click', this._handleDayClick);
        }

        // Add days for current month
        const today = new Date();
        const isToday = (day) =>
            today.getUTCDate() === day &&
            today.getUTCMonth() === this._currentMonth &&
            today.getUTCFullYear() === this._currentYear;

        const isSelected = (day) => {
            if (this._mode === 'single' && this._selectedDate) {
                return this._selectedDate.getUTCDate() === day &&
                    this._selectedDate.getUTCMonth() === this._currentMonth &&
                    this._selectedDate.getUTCFullYear() === this._currentYear;
            } else if (this._selectedDates.length) {
                return this._selectedDates.some(date =>
                    date.getUTCDate() === day &&
                    date.getUTCMonth() === this._currentMonth &&
                    date.getUTCFullYear() === this._currentYear
                );
            }
            return false;
        };

        const isInRange = (day) => {
            if (this._mode !== 'range' || this._selectedDates.length !== 2) return false;

            const date = new Date(this._currentYear, this._currentMonth, day);
            return date > this._selectedDates[0] && date < this._selectedDates[1];
        };

        const isDisabled = (day) => {
            const date = new Date(this._currentYear, this._currentMonth, day);

            if (this._minDate && date < new Date(this._minDate.setUTCHours(0, 0, 0, 0))) {
                return true;
            }

            if (this._maxDate && date > new Date(this._maxDate.setUTCHours(23, 59, 59, 999))) {
                return true;
            }

            return false;
        };

        for (let i = 1; i <= daysInMonth; i++) {
            const day = document.createElement('div');
            day.className = 'day';

            if (isToday(i)) {
                day.classList.add('today');
            }

            if (isSelected(i)) {
                day.classList.add('selected');
            }

            if (isInRange(i)) {
                day.classList.add('in-range');
            }

            if (isDisabled(i)) {
                day.classList.add('disabled');
            }

            day.textContent = i;
            day.dataset.day = i;
            day.dataset.month = this._currentMonth;
            day.dataset.year = this._currentYear;
            daysGrid.appendChild(day);

            day.addEventListener('click', this._handleDayClick);
        }

        // Fill remaining slots with days from next month
        const totalCells = 42; // 6 rows x 7 days
        const remainingCells = totalCells - (daysFromPrevMonth + daysInMonth);
        const nextMonth = this._currentMonth === 11 ? 0 : this._currentMonth + 1;
        const nextMonthYear = this._currentMonth === 11 ? this._currentYear + 1 : this._currentYear;

        for (let i = 1; i <= remainingCells; i++) {
            const day = document.createElement('div');
            day.className = 'day other-month';
            day.textContent = i;
            day.dataset.day = i;
            day.dataset.month = nextMonth;
            day.dataset.year = nextMonthYear;
            daysGrid.appendChild(day);

            day.addEventListener('click', this._handleDayClick);
        }
    }

    _setDate(date) {
        this._selectedDate = new Date(date);
        this._selectedDates = [this._selectedDate];
        this._updateInputValue();
        this._renderCalendarDays();

        // Fire change event
        this._fireChangeEvent();
    }

    _updateInputValue() {
        if (!this._elements.input) return;

        if (this._selectedDates.length === 0) {
            this._elements.input.value = '';
            return;
        }

        if (this._mode === 'single') {
            this._elements.input.value = this._formatDate(this._selectedDate);
        } else if (this._mode === 'multiple') {
            this._elements.input.value = this._selectedDates
                .map(date => this._formatDate(date))
                .join(', ');
        } else if (this._mode === 'range') {
            if (this._selectedDates.length === 1) {
                this._elements.input.value = this._formatDate(this._selectedDates[0]);
            } else {
                this._elements.input.value = `${this._formatDate(this._selectedDates[0])} - ${this._formatDate(this._selectedDates[1])}`;
            }
        }

        // Update time if enabled
        if (this._enableTime && this._selectedDate) {
            const hours = this._selectedDate.getUTCHours().toString().padStart(2, '0');
            const minutes = this._selectedDate.getUTCMinutes().toString().padStart(2, '0');


            // if (this._enableSeconds) {
            const seconds = this._selectedDate.getUTCSeconds().toString().padStart(2, '0');
            this._elements.timePicker.value = `${hours}:${minutes}:${seconds}`;
            // } else {
            //     this._elements.timePicker.value = `${hours}:${minutes}`;
            // }
        }
    }

    /**
     * Parses a date string as UTC, regardless of the local timezone.
     * Strings with an explicit offset (Z or ±HH:MM) are used as-is.
     * Strings without a timezone (e.g. '2026-03-28 20:00:00') are treated as UTC.
     *
     * @param {string} str
     * @returns {Date}
     */
    _parseDateAsUTC(str) {
        if (!str || typeof str !== 'string') return str;
        // Already has explicit timezone info — use as-is
        if (/Z$|[+-]\d{2}:\d{2}$/.test(str)) {
            return new Date(str);
        }
        // No timezone — normalize space separator and force UTC
        return new Date(str.replace(' ', 'T') + 'Z');
    }

    _formatDate(date) {
        if (!date) return '';

        // date is always a Date object here — do NOT call _parseDateAsUTC

        const day = date.getUTCDate();
        const month = date.getUTCMonth() + 1;
        const year = date.getUTCFullYear();
        const hours = date.getUTCHours();
        const minutes = date.getUTCMinutes();
        const seconds = date.getUTCSeconds();

        let formattedDate = this._dateFormat
            .replace('dd', day.toString().padStart(2, '0'))
            .replace('d', day)
            .replace('mm', month.toString().padStart(2, '0'))
            .replace('m', month)
            .replace('yyyy', year)
            .replace('yy', year.toString().slice(-2));

        if (this._enableTime) {
            const time = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
            formattedDate += ' ' + time;
        }

        return formattedDate;
    }

    _hasTime(stringValue) {
        try {
            const tmp = stringValue.split(" ");
            return tmp.length > 1;
        } catch (e) {}
        return false;
    }

    _formatDateForComparison(date) {
        // Create a simple string representation for date comparison
        if (!date) return '';
        return `${date.getUTCFullYear()}-${date.getUTCMonth()}-${date.getUTCDate()}`;
    }

    _fireChangeEvent() {
        const event = new CustomEvent('datepicker-change', {
            detail: {
                selectedDates: this._selectedDates,
                dateStr: this._elements.input.value
            },
            bubbles: true,
            composed: true
        });
        this.dispatchEvent(event);
    }
}

// Define the custom element
customElements.define('swiftref-date-input', VanillaDatePicker);

// Example usage:
/*
<vanilla-datepicker
  date-format="mm/dd/yyyy"
  enable-time
  mode="range"
  theme="dark">
</vanilla-datepicker>

// JavaScript
const datePicker = document.querySelector('vanilla-datepicker');
datePicker.addEventListener('datepicker-change', (event) => {
  console.log('Selected dates:', event.detail.selectedDates);
  console.log('Date string:', event.detail.dateStr);
});
*/
